import React, { useRef, useEffect, useCallback, Children, cloneElement, Fragment } from 'react';
import { debounce } from 'lodash';
import cx from 'classnames';
import useClickOutsideObserver from '@move-web-essentials-hooks/use-click-outside-observer';
import styles from './DesktopMenu.module.css';
import { useDropdownKeyboardSupport } from '../hooks/useDropdownKeyboardSupport';
import { scrollListToSelectedOption } from '../utils/scrollListToSelectedOption';
import { focusOption } from '../utils/focusOption';
import { Search } from '../Search/Search';
const DEBOUNCE_DELAY = 200;
const border = 1;
const margin = 12;
const padding = 8;
const itemHeight = 40;
export const DesktopMenu = ({ open, onClose, minMenuWidth, hasSections, children, 'data-testid': testId, footer, multiple, searchProps, noResultsComponent, scrollTopKey }) => {
    const ref = useRef(null);
    const { onKeyDown, optionsRef } = useDropdownKeyboardSupport();
    useEffect(() => {
        if (ref.current) {
            ref.current.scrollTop = 0;
        }
    }, [scrollTopKey]);
    useClickOutsideObserver(ref, () => open && window.innerWidth >= 640 && onClose());
    useEffect(() => {
        var _a;
        if (!open || window.innerWidth < 640 || !ref.current) {
            return;
        }
        if ((_a = searchProps === null || searchProps === void 0 ? void 0 : searchProps.inputRef) === null || _a === void 0 ? void 0 : _a.current) {
            ref.current.scrollTop = 0;
            return;
        }
        scrollListToSelectedOption(ref.current, optionsRef.current);
    }, [open, optionsRef, searchProps === null || searchProps === void 0 ? void 0 : searchProps.inputRef]);
    const handleResize = useCallback(() => {
        const menu = ref.current;
        if (!menu) {
            return;
        }
        if (!open) {
            menu.style.maxHeight = '0';
            return;
        }
        const rect = menu.getBoundingClientRect();
        if (window.innerHeight - rect.top - menu.scrollHeight < padding) {
            const maxHeight = Math.max(window.innerHeight - rect.top - padding, margin * 2 + itemHeight * 6);
            menu.style.maxHeight = `${maxHeight}px`;
            return;
        }
        if (menu.style.maxHeight === '0px') {
            menu.style.maxHeight = `${menu.scrollHeight + border}px`;
            // The menu height will increase only on the next frame so we must recheck then
            setTimeout(handleResize);
        }
    }, [open]);
    useEffect(() => {
        handleResize();
        if (!open) {
            return undefined;
        }
        const debouncedResize = debounce(handleResize, DEBOUNCE_DELAY);
        window.addEventListener('resize', debouncedResize);
        return () => {
            window.removeEventListener('resize', debouncedResize);
            debouncedResize.cancel();
        };
    }, [open, handleResize]);
    useEffect(() => {
        if (!open || !(searchProps === null || searchProps === void 0 ? void 0 : searchProps.inputRef)) {
            return undefined;
        }
        // On FireFox the menu gets scrolled incorrectly after focusing the search input
        // to fix that we scroll the menu up after the focus is applied
        // cant use 0 timeout as it doesnt always work due to rendering slowdowns
        const timeout = setTimeout(() => { var _a; return (_a = ref.current) === null || _a === void 0 ? void 0 : _a.scrollTo(0, 0); }, 17);
        return () => clearTimeout(timeout);
    }, [open, searchProps === null || searchProps === void 0 ? void 0 : searchProps.inputRef]);
    return (React.createElement("ul", { "data-testid": testId ? `${testId}DesktopMenu` : undefined, className: cx(styles.menu, {
            [styles.closed]: !open,
            [styles.hasSections]: hasSections && !searchProps
        }), style: { minWidth: minMenuWidth }, ref: ref, role: "listbox", "aria-hidden": !open, "aria-multiselectable": multiple, onKeyDown: (event) => {
            var _a, _b;
            return !((_b = (_a = searchProps === null || searchProps === void 0 ? void 0 : searchProps.inputRef) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.contains(event.target)) &&
                onKeyDown(event);
        } },
        searchProps && (React.createElement(Fragment, null,
            React.createElement(Search, Object.assign({}, searchProps, { autoFocus: open, key: `${open}` })),
            searchProps.value.length > 0 &&
                Children.count(children) < 1 &&
                noResultsComponent)),
        Children.map(children, (child, index) => cloneElement(child, {
            ref: (childRef) => {
                optionsRef.current[index] = childRef;
            },
            onClick: () => {
                var _a, _b;
                child.props.onClick();
                // Refocus search after clicking on an option if search is still active
                if ((searchProps === null || searchProps === void 0 ? void 0 : searchProps.value.length) === 0) {
                    return;
                }
                (_b = (_a = searchProps === null || searchProps === void 0 ? void 0 : searchProps.inputRef) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.focus();
            },
            focusPrevious: () => focusOption(ref.current, optionsRef.current[index - 1]),
            focusNext: () => focusOption(ref.current, optionsRef.current[index + 1])
        })),
        footer));
};
//# sourceMappingURL=DesktopMenu.js.map